import type { IndexableDocument } from "@/types/search/IndexableDocument";
import type { SearchRequest } from "@/types/search/SearchRequest";

/** Newest `created` ISO string in a document list, or null when empty. */
export function getNewestCreated(documents: IndexableDocument[]): string | null {
	let newest: string | null = null;
	for (const document of documents) {
		if (document?.created && (newest === null || document.created > newest)) {
			newest = document.created;
		}
	}
	return newest;
}

/**
 * Lower bound for a create/update delta fetch: one second before the newest loaded
 * item so the boundary item is re-fetched (`created >= startDate`),
 * letting the identity merge refresh it. Returns null when there is nothing loaded yet.
 */
export function deltaStartDate(newestCreated: string | null): Date | null {
	if (!newestCreated) return null;
	return new Date(new Date(newestCreated).getTime() - 1000);
}

/**
 * Merge a delta page into the existing list: dedupe by canonical search-result identity (delta entry wins so
 * re-fetched items refresh in place), then sort `created` descending to match the
 * timeline's orderBy. Pure — never mutates inputs.
 */
export function mergeTimelineDelta(existing: IndexableDocument[], delta: IndexableDocument[]): IndexableDocument[] {
	const byIdentity = new Map<string, IndexableDocument>();
	for (const document of existing) byIdentity.set(document.searchResultId ?? document.id, document);
	for (const document of delta) {
		// LSP-16761: an IDLE poll still returns at least one item — `deltaStartDate` deliberately
		// rewinds one second so the boundary item is re-fetched — and it arrives as a fresh object
		// from JSON.parse. Blindly overwriting an unchanged item with an equal-but-new object churns
		// its reference for no reason: it defeats the caller's unchanged-check (see
		// `timelineDocumentsChanged`) and re-renders that tile, which re-requests its thumbnail and
		// produces the visible flash. Keep the existing object when the payload is identical; the
		// delta still wins whenever the content actually differs, so real refreshes are unaffected.
		const identity = document.searchResultId ?? document.id;
		const current = byIdentity.get(identity);
		if (current && documentsAreEqual(current, document, true)) continue;
		byIdentity.set(identity, document);
	}
	return Array.from(byIdentity.values()).sort((left, right) =>
		left.created < right.created ? 1 : left.created > right.created ? -1 : 0
	);
}

/**
 * Structural equality for two indexed documents. Key order is not assumed (both sides are parsed
 * from the same API shape, but relying on that would be a silent footgun), and nested arrays/objects
 * are compared by value. Only ever called for identities present in BOTH lists — normally the single
 * boundary item — so this stays O(1) per poll rather than a whole-list deep compare.
 */
/**
 * Fields contact-api RE-SIGNS on every single search response (`IndexService.hydrateSnapsots`
 * calls `s3Service.getSignedUrl` per request), so their query string carries a fresh signature and
 * date even when the underlying S3 object is untouched. Comparing them verbatim makes every idle
 * poll look like a change, which reassigns the list and re-points each <img src> — the browser then
 * re-fetches a thumbnail it already has, producing the residual flash. Compare them by origin+path
 * so only a genuinely different object counts. Keeping the previously issued URL is safe: they are
 * minted with AWS_S3_SIGNED_URL_EXPIRATION_IN_SECONDS = 86400 (24h), far beyond any session, and a
 * real content change still swaps in the fresh document with its new URL.
 */
const RESIGNED_URL_KEYS = new Set(["mediaUrl", "thumbnailUrl"]);

/**
 * Fields the CLIENT writes onto a document in place, which the API payload never carries.
 * `Timeline`'s IntersectionObserver sets `visible = true` directly on `props.mediaItems` for every
 * intersecting row (plus the first 30) to drive lazy video loading — so an on-screen row gains a key
 * the incoming payload lacks. Counting that as a difference made equality fail for EXACTLY the rows
 * the user can see, which is why the flash survived on the top rows only while off-screen rows were
 * already stable (Cursor Bugbot, PR #2460). Ignored at the document root only; nested objects are
 * still compared in full.
 */
const CLIENT_ONLY_KEYS = new Set(["visible"]);
const NO_IGNORED_KEYS: ReadonlySet<string> = new Set();

function sameResignedUrl(left: unknown, right: unknown): boolean {
	if (left === right) return true;
	if (typeof left !== "string" || typeof right !== "string") return false;
	try {
		const leftUrl = new URL(left);
		const rightUrl = new URL(right);
		return leftUrl.origin === rightUrl.origin && leftUrl.pathname === rightUrl.pathname;
	} catch {
		// Not absolute URLs (relative path, empty string) — fall back to exact comparison.
		return false;
	}
}

function documentsAreEqual(left: unknown, right: unknown, isDocumentRoot: boolean = false): boolean {
	if (left === right) return true;
	if (left === null || right === null || typeof left !== "object" || typeof right !== "object") return false;
	const leftIsArray = Array.isArray(left);
	if (leftIsArray !== Array.isArray(right)) return false;
	if (leftIsArray) {
		const leftItems = left as unknown[];
		const rightItems = right as unknown[];
		return leftItems.length === rightItems.length && leftItems.every((item, index) => documentsAreEqual(item, rightItems[index]));
	}
	const leftRecord = left as Record<string, unknown>;
	const rightRecord = right as Record<string, unknown>;
	const ignored = isDocumentRoot ? CLIENT_ONLY_KEYS : NO_IGNORED_KEYS;
	const leftKeys = Object.keys(leftRecord).filter((key) => !ignored.has(key));
	const rightKeys = Object.keys(rightRecord).filter((key) => !ignored.has(key));
	if (leftKeys.length !== rightKeys.length) return false;
	return leftKeys.every((key) => {
		if (!Object.prototype.hasOwnProperty.call(rightRecord, key)) return false;
		if (RESIGNED_URL_KEYS.has(key)) return sameResignedUrl(leftRecord[key], rightRecord[key]);
		return documentsAreEqual(leftRecord[key], rightRecord[key]);
	});
}

/**
 * LSP-16761: true when a freshly merged delta list is materially different from the
 * current one, so the caller can SKIP reassigning the reactive media array when an idle
 * poll brought nothing new — avoiding a needless grid re-render (the ~60s "flash").
 *
 * Detection is by element identity + order. That is only exact because `mergeTimelineDelta`
 * PRESERVES the existing object for a re-fetched item whose payload is unchanged — without
 * that, the boundary item `deltaStartDate` re-fetches on every idle poll would arrive as a
 * fresh JSON object and make this report "changed" every single time (the original LSP-16761
 * fix shipped with exactly that hole, so the guard never fired and the flash remained).
 * So a length match plus position-wise reference equality means "nothing changed"; any added,
 * removed, reordered, or genuinely modified item breaks it and is reported as changed.
 */
export function timelineDocumentsChanged(current: IndexableDocument[], merged: IndexableDocument[]): boolean {
	if (current.length !== merged.length) return true;
	for (let index = 0; index < current.length; index++) {
		if (current[index] !== merged[index]) return true;
	}
	return false;
}

/**
 * Full-reload counterpart to `mergeTimelineDelta`'s identity preservation.
 *
 * A full reload (`loadTimeline`) replaces the entire list with freshly parsed objects carrying
 * freshly signed S3 URLs, so EVERY visible tile's <img src> changes and the browser re-fetches
 * images it already has — the same flash the delta path was fixed for, via a different route
 * (the periodic reconcile escalates to a full reload whenever the session holds a recording with
 * an unfinished upload, so an idle page can sit on this path indefinitely).
 *
 * Returns the incoming list with each materially-unchanged entry swapped back to the object
 * already on screen, so Vue patches nothing for it. Added, removed, reordered, or genuinely
 * changed entries keep the incoming object, so a full reload stays authoritative.
 */
export function preserveUnchangedDocuments(current: IndexableDocument[], incoming: IndexableDocument[]): IndexableDocument[] {
	if (current.length === 0 || incoming.length === 0) return incoming;
	// Canonical search-result identity (matches mergeTimelineDelta/reconcileTimelinePages below): raw
	// `.id` alone can collide two distinct sibling async/ark recordings that share a `sourceId`, which
	// would substitute the wrong object for a content-equal sibling (ls-heroku-ci).
	const identityOf = (document: IndexableDocument) => document.searchResultId ?? document.id;
	const currentByIdentity = new Map(current.map((document) => [identityOf(document), document]));
	return incoming.map((document) => {
		const existing = currentByIdentity.get(identityOf(document));
		return existing && documentsAreEqual(existing, document, true) ? existing : document;
	});
}

/** The first page of a scheduler-driven reload: `documents` is the newest window; `count` is the server's grand total. */
export interface TimelineHeadPage {
	documents: IndexableDocument[];
	/** Server grand total. A missing / non-finite value is treated as unknown, never as zero. */
	count: number | null | undefined;
}

/**
 * LSP-19529: bounded HEAD reconcile for scheduler-driven (socket / upload-status poll / reconnect /
 * periodic) full reloads.
 *
 * Before this, every "full" flush ran the authoritative multi-page reconcile (`reconcileTimelinePages`),
 * re-fetching the whole scrolled window — two to six `/search/media` pages per flush per tab. In the
 * 2026-09-03 crowd test that fan-out (7 tabs x pages x one flush per ~10s, each page two heavy Postgres
 * statements) drove the staging database to a load average of 37 and every request to 5-10s.
 *
 * New media lands at the TOP of a `created DESC` timeline, so one page from the top is authoritative for
 * everything at or above its oldest row. This merge takes that page as the truth for its window and keeps
 * the already-loaded tail below it untouched: rows in the window that the page did not return are gone
 * (deleted, or currently a post-filtered stub — exactly what a full reload would also have dropped), rows
 * strictly older than the window are retained as-is. Deletions deeper than the first page are picked up
 * by the user's explicit reloads and the multi-page reconcile paths that still use `loadTimeline(true)`.
 *
 * When the page holds the entire result set (as many rows as the server total) it IS the whole list and
 * simply replaces it. A page merely shorter than the page size is NOT taken as proof of that, because the
 * search endpoint post-filters pending stubs and can return a short first page over a long tail. Object identity of unchanged rows is preserved via
 * `preserveUnchangedDocuments` so the grid does not re-render rows that did not change. Pure.
 */
export function mergeTimelineHead(existing: IndexableDocument[], head: TimelineHeadPage, headPageSize: number): IndexableDocument[] {
	const identityOf = (document: IndexableDocument) => document.searchResultId ?? document.id;
	// break-it round 1: collapse repeated canonical identities INSIDE the page and INSIDE the retained tail
	// too (first occurrence wins), matching mergeTimelineDelta/reconcileTimelinePages — sibling async/ark
	// recordings can share a searchResultId, and a duplicate that survived here would render twice.
	const dedupeByIdentity = (documents: IndexableDocument[]): IndexableDocument[] => {
		const seen = new Set<string>();
		return documents.filter((document) => {
			const identity = identityOf(document);
			if (seen.has(identity)) return false;
			seen.add(identity);
			return true;
		});
	};
	const headDocuments = dedupeByIdentity(head.documents);
	// break-it round 3: a nullish / non-finite count is UNKNOWN, not zero — treating it as zero made a full
	// page look like the whole list and erased the scrolled tail.
	const serverCount = typeof head.count === "number" && Number.isFinite(head.count) ? head.count : null;
	// Bugbot (PR review): a page SHORTER than the page size is NOT proof that it is the whole list —
	// /search/media post-filters pending stubs (LSP-16618), so during an upload burst the first page can
	// come back under size while hundreds of older rows still exist. Only the server total proves it.
	const headIsWholeList = serverCount !== null && head.documents.length >= serverCount;
	if (existing.length === 0 || headIsWholeList) {
		return preserveUnchangedDocuments(existing, headDocuments);
	}
	const headIdentities = new Set(headDocuments.map(identityOf));
	let windowFloor: string | null = null;
	for (const document of headDocuments) {
		if (document?.created && (windowFloor === null || document.created < windowFloor)) windowFloor = document.created;
	}
	// break-it round 3: with no usable `created` on the page there is no provable window, so nothing below
	// the page can be judged. Fail safe: keep every loaded row the page did not re-return.
	if (windowFloor === null) {
		const unseen = dedupeByIdentity(
			existing.filter((document) => !document.id.startsWith("temp-annotation-") && !headIdentities.has(identityOf(document)))
		);
		return preserveUnchangedDocuments(existing, [...headDocuments, ...unseen]);
	}
	// At or below the window floor: a loaded row whose `created` TIES the floor but is absent from the page
	// is ambiguous (it may sit just past the page boundary in a created-DESC ordering), so it is kept
	// rather than dropped. Rows strictly newer than the floor that the page did not return are gone.
	// break-it round 2: `temp-annotation-…` rows are client-synthetic placeholders (MediaLightBox) the server
	// can never return; loadTimeline(true) excludes them from its reconcile target and a replacement drops
	// them, so a head merge must not carry one forward in the tail either.
	const tail = dedupeByIdentity(
		existing.filter(
			(document) =>
				!document.id.startsWith("temp-annotation-") &&
				!headIdentities.has(identityOf(document)) &&
				windowFloor !== null &&
				document.created <= windowFloor
		)
	);
	return preserveUnchangedDocuments(existing, [...headDocuments, ...tail]);
}

/** Keys that, when present and non-empty, mean the user has narrowed the view (delta is unsafe). */
const ACTIVE_FILTER_KEYS: (keyof SearchRequest)[] = [
	"searchTerm",
	"contentTypes",
	"statuses",
	"customerIds",
	"userIds",
	"labels",
	"startDate",
	"endDate",
	"dateFilter",
	"orderBy",
	"orderByDescending",
	"organization",
	"latitude",
	"longitude"
];

/**
 * Keys that actually NARROW which rows come back, as opposed to merely changing their order or scope.
 *
 * Deliberately a different list from ACTIVE_FILTER_KEYS above, which answers a different question. That one
 * asks "is the delta path unsafe?", and for that, a sort change IS disqualifying — the delta window is
 * computed from the newest `created` and re-sorting invalidates it. But sorting removes no rows, and
 * `organization: "current"` is the default scope rather than a restriction.
 *
 * The distinction matters because Filters.vue spreads its whole request into every emit, and its Clear
 * Filters path explicitly sets `organization: "current"` (Filters.vue:495). Judging "narrowed" with
 * ACTIVE_FILTER_KEYS therefore reported a narrowed view immediately after the user CLEARED their filters —
 * the exact moment a missing row starts to mean something (Cursor Bugbot).
 */
const NARROWING_FILTER_KEYS: (keyof SearchRequest)[] = [
	"searchTerm",
	"contentTypes",
	"statuses",
	"customerIds",
	"userIds",
	"labels",
	"startDate",
	"endDate",
	"dateFilter",
	"latitude",
	"longitude"
];

/**
 * True when the request restricts WHICH rows are returned, so a row's absence from the response proves
 * nothing about whether it still exists. Ordering and the default organization scope are not restrictions.
 */
export function userFiltersNarrowResults(userFilters: Partial<SearchRequest>): boolean {
	return NARROWING_FILTER_KEYS.some((key) => {
		const value = userFilters[key];
		if (value == null || value === "") return false;
		if (Array.isArray(value)) return value.length > 0;
		return true;
	});
}

/** True when any user-applied filter is active, so the create/update delta path must fall back to a full reload. */
export function userFiltersActive(userFilters: Partial<SearchRequest>): boolean {
	return ACTIVE_FILTER_KEYS.some((key) => {
		const value = userFilters[key];
		if (value == null || value === "") return false;
		if (Array.isArray(value)) return value.length > 0;
		return true;
	});
}

/** One page of a reconcile fetch: `documents` is this page's rows; `count` is the server's grand total. */
export interface ReconcilePage {
	documents: IndexableDocument[];
	count: number;
}

/** Fetches page N (1-based). Returns null to abort the reconcile (e.g. a newer reload superseded this run). */
export type FetchReconcilePage = (pageNumber: number) => Promise<ReconcilePage | null>;

export interface ReconcileResult {
	documents: IndexableDocument[];
	count: number;
	/** True when the caller should keep its current list rather than commit this snapshot. */
	inconsistent: boolean;
	/**
	 * Present only when `inconsistent` — which of three operationally distinct causes triggered it, so a
	 * caller logging this doesn't misattribute one to another, and so it can decide whether an immediate
	 * retry is worth attempting:
	 * `"restarts-exhausted"` — the reported total kept changing across every restart attempt (concurrent
	 * writes); transient, likely to settle on retry.
	 * `"incomplete"` — the attempt stopped via an early clean break (empty page, offset exhausted, etc.)
	 * without either re-covering every previously-loaded id or fetching the server's entire current result
	 * set; may resolve on retry (e.g. a temporarily stub-inflated id becomes findable).
	 * `"page-cap-exceeded"` — this SPECIFIC attempt consumed every one of its `maxPages` fetches
	 * (`pageNumber` ran past `maxPages`) without completing. Note this is a property of the attempt, not
	 * simply of `existingDocuments.length`: a deep loaded set that completes early via an id-recovery or
	 * whole-result-set break is `"incomplete"`-eligible like any other case, and conversely a SMALL loaded
	 * set can still land here if every one of the 20 pages comes back empty (persistent stub inflation with
	 * nothing ever hydrating). Retrying immediately reproduces the identical shortfall only insofar as the
	 * underlying server state hasn't changed — but since a fresh attempt right now can't do any better than
	 * this one just did, the caller falls back to its normal cadence rather than retrying on the tight one
	 * (ls-heroku-ci / liveswitch-ai-pr-reviewer: retrying immediately on the tight periodic cadence hot-loops
	 * indefinitely for a conversation whose reconcile structurally can't finish within one attempt).
	 */
	inconsistentReason?: "restarts-exhausted" | "incomplete" | "page-cap-exceeded";
	/** Restart attempts actually consumed before this result was produced (0 if the first attempt decided it). */
	restartsUsed: number;
	/**
	 * Canonical identities from `existingDocuments` that this attempt never turned up in a fetched page
	 * (empty when every previously-loaded id was recovered). Exposed so a caller can tell a TRANSIENT
	 * `"incomplete"` (a different unrecovered set each time — a stub-inflated id that keeps shifting)
	 * from a PERSISTENT one (the identical unrecovered set across consecutive attempts — e.g. a row
	 * `IndexService` permanently post-filters while `count` stays inflated), which never resolves on
	 * retry and should stop being retried on the tight periodic cadence the same way `"page-cap-exceeded"`
	 * already does (ls-heroku-ci).
	 */
	unrecoveredIds: string[];
}

export interface ReconcileOptions {
	/** The documents currently loaded; the reconcile keeps paging until every still-existing loaded id is re-covered (or the list end / page cap is reached). */
	existingDocuments: IndexableDocument[];
	/** Rows per page request (the server clamp). */
	pageSize: number;
	/** Hard cap on pages fetched per attempt. */
	maxPages: number;
	/** Bound on restarts triggered by a mid-reconcile total change. */
	maxRestarts: number;
}

/**
 * LSP-16706: authoritative multi-page reconcile. Fetches sequential pages from the top via
 * `fetchPage` and concatenates them (deduped by id, preserving server page order) until every
 * previously-loaded id is re-covered plus the new head — or the whole result set is fetched, an
 * empty page lands past the loaded depth, every offset up to the total has been requested, or the
 * page cap is hit. A short/empty page is NOT treated as end-of-list before the loaded depth is
 * re-covered, because the API post-filters pending stubs (LSP-16618). If the reported total changes
 * between pages (a concurrent insert/delete shifts every offset) the reconcile restarts from the top
 * on the settled list, bounded by `maxRestarts`; on exhaustion it returns `inconsistent: true` so
 * the caller can keep its last known-good list rather than commit a misaligned snapshot. Returns
 * null if `fetchPage` aborts (returns null).
 */
export async function reconcileTimelinePages(fetchPage: FetchReconcilePage, options: ReconcileOptions): Promise<ReconcileResult | null> {
	const { existingDocuments, pageSize, maxPages, maxRestarts } = options;
	// Canonical search-result identity (matches mergeTimelineDelta above and every consumer in
	// EntityDetailPage.vue): PostgresSearch emits `id` as `sourceId` for async/ark recording types, and
	// one session can legitimately produce sibling recordings sharing a `sourceId`, so raw `.id` alone
	// can collide two genuinely distinct rows (ls-heroku-ci).
	const identityOf = (document: IndexableDocument) => document.searchResultId ?? document.id;
	const existingIds = existingDocuments.map(identityOf);
	for (let reconcileAttempt = 0; ; reconcileAttempt += 1) {
		const idsToRecover = new Set(existingIds);
		const seenIds = new Set<string>();
		const accumulated: IndexableDocument[] = [];
		let latestCount = 0;
		let expectedCount: number | null = null;
		let mutatedMidReconcile = false;
		let pageNumber = 1;
		while (pageNumber <= maxPages) {
			const pageResponse = await fetchPage(pageNumber);
			if (pageResponse === null) {
				return null; // aborted (e.g. superseded by a newer reload)
			}
			latestCount = pageResponse.count;
			const pageDocuments = pageResponse.documents;
			if (expectedCount === null) {
				expectedCount = pageResponse.count;
			} else if (pageResponse.count !== expectedCount) {
				mutatedMidReconcile = true;
				break; // total changed between pages -> the list shifted; restart or bail
			}
			for (const document of pageDocuments) {
				const identity = identityOf(document);
				if (!seenIds.has(identity)) {
					seenIds.add(identity);
					accumulated.push(document);
				}
				idsToRecover.delete(identity);
			}
			if (idsToRecover.size === 0 && accumulated.length >= existingDocuments.length) {
				break; // re-covered every previously-loaded item plus the newer head
			}
			if (accumulated.length >= latestCount) {
				break; // re-fetched the whole result set
			}
			if (pageDocuments.length === 0 && accumulated.length >= existingDocuments.length) {
				break; // empty page after already re-covering the loaded depth
			}
			if (pageNumber * pageSize >= latestCount) {
				break; // requested every offset up to the reported total
			}
			pageNumber += 1;
		}
		if (!mutatedMidReconcile) {
			// A snapshot is authoritative (safe to REPLACE the loaded list) only if the reconcile actually
			// PROVES no still-existing loaded row was missed. Only two conditions prove that: every
			// previously-loaded id actually turned up in a fetched page (any surplus in `accumulated` is
			// legitimately new rows — this is id-level proof), or the reconcile fetched the server's ENTIRE
			// current result set (`accumulated.length >= latestCount`), in which case an id that still
			// didn't turn up is proven gone (deleted, or currently a post-filtered stub) rather than merely
			// unreached.
			//
			// KNOWN, ACCEPTED LIMITATION (ls-heroku-ci finding 5, reviewed and deliberately left as-is —
			// see the PR description's own "Known limitation" section): the second disjunct is unsound for
			// a count-PRESERVING swap landing strictly BETWEEN two page fetches of the SAME attempt (one row
			// deleted, a different row inserted, net total unchanged — `expectedCount`/`mutatedMidReconcile`
			// above only catches the total itself changing). Worked example, page size 2, existing [A,B,C,D]:
			// page 1 returns [A,B], count 4; between fetches A is deleted and E is appended (count stays 4);
			// page 2 returns [D,E], count still 4; accumulated = [A,B,D,E] satisfies the disjunct even though
			// C (still existing) was never fetched. A client observing only {count, documents} per page
			// cannot distinguish this from the ordinary, common, multi-page-shrink-via-deletion case this
			// disjunct exists to serve — restricting it (e.g. to single-page attempts only) was tried and
			// reverted: it incorrectly deferred routine multi-page deletion reconciles (see the reverted
			// commit), regressing the PR's actual purpose to close a narrow, self-healing race that
			// genuinely requires server-side cursor/snapshot support to close soundly.
			const complete = idsToRecover.size === 0 || accumulated.length >= latestCount;
			// "page-cap-exceeded" means THIS attempt genuinely consumed every one of its maxPages fetches
			// without completing (the while loop exited via its condition, not an early break) — checked via
			// `pageNumber > maxPages`, not `existingDocuments.length >= maxPages * pageSize` (Cursor Bugbot):
			// a large previously-loaded depth does not by itself mean retrying is futile — the server total
			// may since have shrunk, or stubs may hydrate, letting a later attempt complete in fewer pages
			// than this one needed. Only "we used the whole budget and still couldn't prove completeness" is
			// evidence retrying against unchanged data reproduces the identical shortfall.
			const pageCapExceeded = !complete && pageNumber > maxPages;
			return {
				documents: accumulated,
				count: latestCount,
				inconsistent: !complete,
				inconsistentReason: complete ? undefined : pageCapExceeded ? "page-cap-exceeded" : "incomplete",
				restartsUsed: reconcileAttempt,
				unrecoveredIds: Array.from(idsToRecover)
			};
		}
		if (reconcileAttempt >= maxRestarts) {
			return {
				documents: accumulated,
				count: latestCount,
				inconsistent: true,
				inconsistentReason: "restarts-exhausted",
				restartsUsed: reconcileAttempt,
				unrecoveredIds: Array.from(idsToRecover)
			};
		}
	}
}
